import { WorkspaceButton, WorkspaceDialog, WorkspaceNotice } from '@/components/ui/workspace';

/**
 * Remove a registered change order from the contract chain (C2-51) — the
 * confirm step for the "Remove this version" action on the Change orders
 * page. Undoes a registration made by mistake: the version leaves the
 * chain and the one before it is current again. The document itself stays
 * in the engagement and reads as a detected change order again.
 *
 * Only the latest version with no applied revisions can go; the server
 * refuses anything else with a 409, and this dialog says so up front when
 * the version already has revisions on the register.
 *
 * Presentational, like `DeleteEngagementModal`: the caller owns the
 * request (`onConfirm`), the pending state and the server's refusal.
 */
export interface RemoveChangeOrderVersionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** "v2" — the version being removed. */
  versionLabel: string;
  /** "v1" — the version that becomes current again. */
  priorVersionLabel: string;
  /** The change-order document's title, when known. */
  documentTitle?: string | null;
  /** Deliverable revisions applied against this version; when > 0 removal is refused. */
  revisionCount: number;
  removing: boolean;
  /** Server refusal, if the last attempt failed. */
  error: string | null;
  onConfirm: () => void;
}

export function RemoveChangeOrderVersionModal({
  open,
  onOpenChange,
  versionLabel,
  priorVersionLabel,
  documentTitle,
  revisionCount,
  removing,
  error,
  onConfirm,
}: RemoveChangeOrderVersionModalProps) {
  const blocked = revisionCount > 0;
  const submit = () => {
    if (!blocked && !removing) onConfirm();
  };

  return (
    <WorkspaceDialog
      open={open}
      onOpenChange={(next) => {
        if (!removing) onOpenChange(next);
      }}
      title={`Remove SOW ${versionLabel}?`}
      description={
        documentTitle
          ? `${documentTitle} leaves the contract chain. SOW ${priorVersionLabel} is the current version again.`
          : `This version leaves the contract chain. SOW ${priorVersionLabel} is the current version again.`
      }
      closeOnScrimClick={!removing}
      actions={
        <>
          <WorkspaceButton size="md" disabled={removing} onClick={() => onOpenChange(false)}>
            Keep the version
          </WorkspaceButton>
          <WorkspaceButton
            size="md"
            variant="destructive"
            disabled={blocked || removing}
            onClick={submit}
          >
            {removing ? 'Removing…' : 'Remove this version'}
          </WorkspaceButton>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-body-sm text-ink-700">
        <div>
          <p className="font-semibold text-ink-900">What removing does</p>
          <ul className="mt-1 list-disc pl-5">
            <li>
              Takes this version out of the contract chain. Deliverables, facts and the AI review
              queue read against SOW {priorVersionLabel} again.
            </li>
            <li>
              Keeps the document. It stays in the engagement and appears under detected change
              orders, so it can be registered again when it should be.
            </li>
            <li>The removal is recorded in the engagement audit trail.</li>
          </ul>
        </div>
        {blocked ? (
          <WorkspaceNotice>
            This version has {revisionCount} applied deliverable revision
            {revisionCount === 1 ? '' : 's'} on the register and cannot be removed.
          </WorkspaceNotice>
        ) : null}
      </div>
      {error ? <WorkspaceNotice tone="critical">{error}</WorkspaceNotice> : null}
    </WorkspaceDialog>
  );
}
